<template>
  <div v-if="!weatherPending" class="weather-tile font-roboto">
    <a
      v-if="!weatherPending"
      :href="`${currentWeather[0]?.Link}&partner=web_onelaunch_ntp_adc`"
      class="weather-tile-link"
      target="_blank"
      @click="weatherTileClick()"
    />
    <div class="weather-tile__time-location">
      <p class="lh-base fs-6 item-value text-start">
        {{ currentLocation?.LocalizedName }}, {{ currentLocation?.AdministrativeArea?.ID }}
      </p>
      <p class="item-label">{{ dayOfWeek }} {{ currentTime }}</p>
      <p v-if="!weatherPending" class="item-label">
        {{ currentWeather[0]?.WeatherText }}
      </p>
    </div>
    <div class="weather-tile__icon-block">
      <LazyClassicNtpWeatherIcon
        v-if="currentWeather[0]?.WeatherIcon"
        :icon="currentWeather[0]?.WeatherIcon"
      />

      <p v-if="temperatureUnit.value == 'F' && !weatherPending" class="weather-tile__icon-block--temperature">
        {{ currentWeather[0]?.Temperature.Imperial.Value }}
        <sup class="unit"
          ><span
            class="unit--hover nonSelectedUnit--color"
            @click="temperatureUnit.updateTemperatureUnit('celcius')"
            >°C</span
          >
          |
          <a
            class="unit--hover selectedUnit--color"
            @click="temperatureUnit.updateTemperatureUnit('fahrenheit')"
            >°F</a
          ></sup
        >
      </p>
      <p v-if="temperatureUnit.value == 'C' && !weatherPending" class="weather-tile__icon-block--temperature">
        {{ currentWeather[0]?.Temperature.Metric.Value }}

        <sup class="unit"
          ><a
            class="unit--hover selectedUnit--color"
            @click="temperatureUnit.updateTemperatureUnit('celcius')"
            >°C</a
          >
          |
          <a
            class="unit--hover nonSelectedUnit--color"
            @click="temperatureUnit.updateTemperatureUnit('fahrenheit')"
            >°F</a
          ></sup
        >
      </p>
    </div>
    <div class="d-flex w-100 weather-tile__details-more">
      <p class="more-text">More from</p>
      <img src="public/images/accuweather_logo.svg?url" />
      <a class="accuweather-text"><u>AccuWeather</u></a>
    </div>
  </div>
</template>

<script setup>
  import { useTemperatureUnit } from '~/store/TemperatureUnitStore'
  import { sendMixpanelEvent } from '~/utils/host-browser-apis'

  const temperatureUnit = useTemperatureUnit()
  const currentWeather = ref([])
  const currentLocation = ref({})
  const weatherPending = ref(true)
  const date = new Date()
  const dayOfWeek = date.toLocaleString('default', { weekday: 'long' })
  const currentTime = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })

  const props = defineProps({
    tileHeight: {
      type: Number,
      default: 250,
    },
    textMargin: {
      type: Number,
      default: 24,
    },
    iconBlockMarginTop: {
      type: Number,
      default: 0,
    },
    detailsMoreMarginTop: {
      type: Number,
      default: -2,
    },
  })

  const tileHeight = props.tileHeight + 'px'
  const textMargin = props.textMargin + 'px'
  const iconBlockMarginTop = props.iconBlockMarginTop + 'px'
  const detailsMoreMarginTop = props.detailsMoreMarginTop + 'px'

  if (process.client) {
    // there is  a bug at the moment in the wrangler 2 so cannot be tested locally but once that is resolved we will be able to test the functions locally but seems to be working with the bug
    // https://github.com/cloudflare/wrangler2/issues/1701

    // Nuxt prevents us from awaiting useFetch client side due to hydration issues, so we're using $fetch instead.
    try {
      const response = await $fetch.raw('/api/weather/geoSearch')
      if (response._data.Key) {
        currentLocation.value = response._data
        const { data: currentWeatherData } = useLazyFetch(
          '/api/weather/currentWeather/' + currentLocation.value.Key,
          { initialCache: false }
        )

        watch(currentWeatherData, (weather) => {
          if (Array.isArray(weather) && weather[0].WeatherText) {
            currentWeather.value.push(weather[0])
            weatherPending.value = false
          }
        })
      }
    } catch {
      // ignore
    }
  }

  const weatherTileClick = async () => {
    await sendMixpanelEvent('NtpWeather_Clicked')
  }
</script>
<style lang="scss" scoped>
  .weather-tile-link {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
  }

  .weather-tile {
    --border-color: #e0e0e0;
    --title-color: #212121;
    --provider-text-color: #212121;
    --selected-unit-color: #212121;

    grid-row: 2 / 4;
    padding: 16px 16px 27px;
    border-radius: 6px;
    border: solid 1px var(--border-color);
    height: v-bind(tileHeight);
    text-decoration: none;
    display: block;
    position: relative;

    &__time-location {
      height: 64px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      margin: 0 41px v-bind(textMargin) 0;
    }

    &__icon-block {
      height: 123px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      margin: v-bind(iconBlockMarginTop) 0 2px;

      &--temperature {
        font-size: 40px;
        font-weight: 600;
        line-height: normal;
        color: var(--provider-text-color);
        margin: 0;
      }
    }

    &__details-item {
      height: 16px;
    }

    &__details-more {
      height: 14px;
      justify-content: center;
      margin-top: v-bind(detailsMoreMarginTop);
    }
  }

  .item-label {
    margin: 0 auto 0 0;
    font-size: 14px;
    color: #616161;
    line-height: normal;
    text-align: start;
    width: max-content;
  }

  .item-value {
    font-size: 14px;
    font-weight: 500;
    color: var(--title-color);
    margin: 0;
    line-height: normal;
    text-align: end;
  }

  .unit {
    font-size: 16px;
    top: -1.5em;

    &--hover {
      cursor: pointer;
      text-decoration: none;
      color: #212121;
    }
  }

  .selectedUnit--color {
    color: var(--selected-unit-color);
  }

  .nonSelectedUnit--color {
    color: #9e9e9e;
  }

  .more-text {
    font-size: 12px;
    line-height: normal;
    text-align: left;
    color: var(--provider-text-color);
    margin-right: 8px;
  }

  .accuweather-text {
    font-size: 12px;
    font-weight: 500;
    line-height: normal;
    text-align: right;
    color: var(--provider-text-color);
    margin-left: 4px;
  }

  .dark .weather-tile {
    --border-color: #9d9ea11a;
    --title-color: #f5f5f6;
    --provider-text-color: #9d9ea1;
    --selected-unit-color: #727377;
  }
</style>
